import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Colors, Text } from '@mercator-gcp/grout';
import { capitalize, escapeRegExp } from 'lodash';
import { useNavigate } from 'react-router-dom';
import CompanyList from 'components/elements/CompanyList';
import { PermitChip } from 'components/elements/PermitChip';
import { FlexRow } from 'components/layout/FlexRow';
import { DocumentRelatedCompaniesContent } from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import PermitNumAndNumberOfEvents from 'components/widgets/ProjectDetails/ProjectActivityCard/PermitNumAndNumberOfEvents';
import StatusSummary from 'components/widgets/ProjectDetails/ProjectActivityCard/StatusSummary';
import { useGetModalActions } from 'hooks/useModal';
import { ActivitySummary } from 'types/ActivitySummary';
import {
  isActivitySummaryComplete,
  isSummaryWithMultipleActivities,
} from 'utils/activity';
import { trackViewStageDetails } from 'utils/analytics';
import {
  ProjectGroupTypes,
  ViewStageDetailsClickLocations,
} from 'utils/analytics/constants';
import { combineCompaniesAndRelatedCompanies } from 'utils/companiesInvolved';
import { calcTimeAgo, formatDate } from 'utils/date';
import type { Project } from 'types';

const activityContainerStyle = (isClickable: boolean) => ({
  _hover: {
    cursor: isClickable ? 'pointer' : 'default',
  },
});

const companyListStyle = { marginTop: 'padding_8' };

const activityDescription = {
  marginTop: 'padding_8',
};

const titleStyle = {
  justifyContent: 'flex-start',
  alignItems: 'center',
  columnGap: 'padding_8',
};

const footerStyle = {
  marginTop: 'padding_8',
  justifyItems: 'space-between',
};

const footerSummaryStyle = {
  columnGap: 'padding_8',
};

const relatedCompanyContactsStyle = {
  marginTop: 'padding_8',
  marginBottom: 'padding_8',
};

type Props = {
  activitySummary: ActivitySummary;
  projectSlug?: Project['slug'];
  showCompanyContacts?: boolean;
};

const ProjectActivityCard = ({
  projectSlug,
  activitySummary,
  showCompanyContacts = false,
}: Props) => {
  const navigate = useNavigate();
  const latestActivity = activitySummary.latest_activity;

  const { Skeleton } = useGetModalActions(false);

  const isComplete = isActivitySummaryComplete(activitySummary);
  const hasPriorEvents = isSummaryWithMultipleActivities(activitySummary);

  const dateDisplay = useMemo(() => {
    const nowISODate = new Date().toISOString();

    return `${calcTimeAgo(activitySummary.date, nowISODate)} • ${formatDate(
      activitySummary.latest_activity.date,
    )}`;
  }, [activitySummary.date, activitySummary.latest_activity.date]);

  const handleActivityClick = () => {
    trackViewStageDetails({
      projectId: projectSlug,
      groupId: String(0),
      groupType: ProjectGroupTypes.UNCATEGORIZED,
      stageId: latestActivity.document_uuid,
      stageType: latestActivity.document_type,
      clickLocation: ViewStageDetailsClickLocations.PDP,
      clickText: undefined,
    });
    const modalQueryParameters = `?document-type=${latestActivity.document_type_code}&document-id=${latestActivity.document_id}`;
    navigate(modalQueryParameters);
  };

  const activityHasCompanies = useMemo(() => {
    return latestActivity?.companies?.length > 0;
  }, [latestActivity?.companies?.length]);

  const filteredCompanies = useMemo(
    () =>
      combineCompaniesAndRelatedCompanies(
        latestActivity.related_contacts,
        latestActivity.companies,
      ),
    [latestActivity.companies, latestActivity.related_contacts],
  );

  const viewMoreDetailsLabel = useMemo(() => {
    if (!latestActivity.related_contacts?.length) {
      return 'View all Details';
    }
    if (latestActivity.related_contacts?.length === 1) {
      return `View ${1} Contact Detail`;
    }

    return `View ${latestActivity.related_contacts.length} Contact Details`;
  }, [latestActivity]);

  const formattedDescription = useMemo(() => {
    // Remove project_name from description to avoid repetition
    if (!latestActivity.description) return '';

    if (!latestActivity.project_name) return latestActivity.description.trim();

    // Escape the project name: it can contain regex metacharacters (*, (, +, ...)
    // which would otherwise throw when compiled into a RegExp.
    const projectNameRegex = new RegExp(escapeRegExp(latestActivity.project_name), 'i');

    return latestActivity.description.replace(projectNameRegex, '').trim();
  }, [latestActivity.description, latestActivity.project_name]);

  return (
    <Box
      key={latestActivity.project_id}
      onClick={handleActivityClick}
      sx={activityContainerStyle(!!latestActivity?.permitnum)}
    >
      <FlexRow sx={titleStyle}>
        <PermitChip type={latestActivity.document_type}>
          {latestActivity.document_type}
        </PermitChip>
        <PermitNumAndNumberOfEvents
          activityPermitNumber={
            latestActivity.permitnum || latestActivity.document_uuid || null
          }
          numberOfEvents={activitySummary.count}
        />
      </FlexRow>
      {activityHasCompanies && (
        <FlexRow sx={companyListStyle}>
          <CompanyList companyList={filteredCompanies} />
        </FlexRow>
      )}
      {latestActivity.project_name && (
        <Text as="p" sx={activityDescription}>
          {capitalize(latestActivity.project_name)}
        </Text>
      )}
      {latestActivity.description && (
        <Text as="p" sx={activityDescription}>
          {capitalize(formattedDescription)}
        </Text>
      )}
      <FlexRow sx={footerStyle}>
        <FlexRow sx={footerSummaryStyle}>
          <StatusSummary
            isComplete={isComplete}
            hasPriorEvents={hasPriorEvents}
            status={latestActivity.status}
          />
          <Text color={Colors.TextQuiet}>{dateDisplay}</Text>
        </FlexRow>
        <Button variant="secondary">{viewMoreDetailsLabel}</Button>
      </FlexRow>
      {showCompanyContacts && (
        <Box sx={relatedCompanyContactsStyle}>
          <DocumentRelatedCompaniesContent
            contacts={
              typeof latestActivity.related_contacts === 'string'
                ? []
                : latestActivity.related_contacts
            }
            documentId={latestActivity.document_id}
            Skeleton={Skeleton}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProjectActivityCard;
